import React, { useCallback, useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import DateInput from '../components/DateInput';
import { formatCurrency } from '../utils/currency';
import { formatDate, formatDateTime, todayIso } from '../utils/dates';
import KebabMenu, { MENU_ICONS, useKebabMenu } from '../components/KebabMenu';
import './Expenses.css';

const CATEGORY_SUGGESTIONS = [
  'Light bill',
  'Water',
  'Rent',
  'Internet',
  'Salary',
  'Transport',
  'Maintenance',
  'Other'
];

const PAID_VIA = [
  { value: 'cash', label: 'Cash' },
  { value: 'online', label: 'Online' },
  { value: 'other', label: 'Other' }
];

const paidViaLabel = (value) => PAID_VIA.find((row) => row.value === value)?.label || 'Other';

const emptyForm = () => ({
  id: null,
  category: '',
  amount: '',
  expense_date: todayIso(),
  paid_via: 'cash',
  notes: ''
});

const Expenses = () => {
  const [summary, setSummary] = useState({ total: 0, count: 0, by_category: [] });
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [category, setCategory] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [openActionMenu, setOpenActionMenu] = useKebabMenu();

  const isEditing = Boolean(form.id);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (category) params.category = category;
      const [summaryRes, listRes] = await Promise.all([
        axios.get('/api/expenses/summary', { params }),
        axios.get('/api/expenses', { params })
      ]);
      setSummary({
        total: Number(summaryRes.data.total) || 0,
        count: Number(summaryRes.data.count) || 0,
        by_category: summaryRes.data.by_category || []
      });
      setExpenses(listRes.data.expenses || []);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [from, to, category]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      category: row.category || '',
      amount: String(row.amount ?? ''),
      expense_date: String(row.expense_date || '').slice(0, 10),
      paid_via: row.paid_via || 'other',
      notes: row.notes || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setForm(emptyForm());
  };

  const handleDelete = async (row) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`/api/expenses/${row.id}`);
      await load();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete expense');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('Enter a valid amount greater than 0');
      return;
    }
    if (!String(form.category || '').trim()) {
      alert('Category is required');
      return;
    }
    if (!form.expense_date) {
      alert('Date is required');
      return;
    }

    const payload = {
      amount,
      expense_date: form.expense_date,
      category: String(form.category).trim(),
      paid_via: form.paid_via || 'other',
      notes: String(form.notes || '').trim() || null
    };

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`/api/expenses/${form.id}`, payload);
      } else {
        await axios.post('/api/expenses', payload);
      }
      setShowModal(false);
      setForm(emptyForm());
      await load();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const categoryOptions = Array.from(new Set([
    ...CATEGORY_SUGGESTIONS,
    ...summary.by_category.map((row) => row.category).filter(Boolean)
  ]));

  if (loading && expenses.length === 0 && summary.count === 0) {
    return (
      <div className="page-container">
        <div className="container">Loading…</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container expenses-page">
        <div className="page-header">
          <div>
            <h1>Expenses</h1>
            <p className="expenses-subtitle">
              Track light bills, rent, and other costs. This list is separate from Cash Book.
            </p>
          </div>
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            Add expense
          </button>
        </div>

        <div className="expenses-summary-grid">
          <div className="expenses-summary-card expenses-total">
            <span className="expenses-summary-label">Total</span>
            <strong>{formatCurrency(summary.total)}</strong>
          </div>
          <div className="expenses-summary-card">
            <span className="expenses-summary-label">Entries</span>
            <strong>{summary.count}</strong>
          </div>
          <div className="expenses-summary-card">
            <span className="expenses-summary-label">Categories</span>
            <strong>{summary.by_category.length}</strong>
          </div>
        </div>

        {summary.by_category.length > 0 && (
          <div className="expenses-categories">
            {summary.by_category.map((row) => (
              <button
                key={row.category}
                type="button"
                className={`expenses-category-chip${category === row.category ? ' active' : ''}`}
                onClick={() => setCategory(category === row.category ? '' : row.category)}
              >
                <span>{row.category}</span>
                <strong>{formatCurrency(row.total)}</strong>
              </button>
            ))}
          </div>
        )}

        <div className="expenses-filters">
          <div className="form-group">
            <label>From</label>
            <DateInput value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="form-group">
            <label>To</label>
            <DateInput value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All</option>
              {categoryOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => { setFrom(''); setTo(''); setCategory(''); }}
          >
            Clear
          </button>
        </div>

        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Category</th>
                <th>Amount</th>
                <th>Paid via</th>
                <th>Notes</th>
                <th>By</th>
                <th>Logged</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }}>No expenses yet</td>
                </tr>
              ) : (
                expenses.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDate(row.expense_date)}</td>
                    <td>{row.category}</td>
                    <td>{formatCurrency(row.amount)}</td>
                    <td>{paidViaLabel(row.paid_via)}</td>
                    <td>{row.notes || '—'}</td>
                    <td>{row.created_by_label || '—'}</td>
                    <td>{formatDateTime(row.created_at)}</td>
                    <td>
                      <KebabMenu
                        menuId={`expense-${row.id}`}
                        openId={openActionMenu}
                        setOpenId={setOpenActionMenu}
                        items={[
                          { label: 'Edit', icon: MENU_ICONS.edit, onClick: () => openEdit(row) },
                          { label: 'Delete', icon: MENU_ICONS.trash, danger: true, onClick: () => handleDelete(row) }
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isEditing ? 'Edit expense' : 'Add expense'}</h2>
              <button type="button" className="close-btn" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Category *</label>
                <input
                  type="text"
                  list="expense-categories"
                  required
                  autoFocus
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="e.g. Light bill"
                />
                <datalist id="expense-categories">
                  {CATEGORY_SUGGESTIONS.map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
              </div>
              <div className="form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  min="0.001"
                  step="0.001"
                  required
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.000"
                />
              </div>
              <div className="form-group">
                <label>Date *</label>
                <DateInput
                  required
                  value={form.expense_date}
                  onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Paid via</label>
                <select
                  value={form.paid_via}
                  onChange={(e) => setForm({ ...form, paid_via: e.target.value })}
                >
                  {PAID_VIA.map((row) => (
                    <option key={row.value} value={row.value}>{row.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Bill number, month, or other detail"
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (isEditing ? 'Update' : 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
